/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { isRightToLeft } from 'js/lib/language';

import { IconButton } from '@coursera/cds-core';

import _t from 'i18n!nls/front-page';

type Props = {
  children: React.ReactNode[];
};

const styles = {
  container: css`
    margin: var(--cds-spacing-100) 1vw;
  `,
  horizontalScrollContainer: css`
    white-space: nowrap;
    position: relative;
    overflow-x: scroll;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    display: flex;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  `,
  card: css`
    width: 91vw;
    margin: 0 1vw;
    height: 100%;
  `,
  cardContainer: css`
    scroll-snap-align: start;
  `,
  controls: css`
    display: flex;
    justify-content: space-between;
    width: 91vw;
    margin: var(--cds-spacing-100) 1vw 0;
  `,
  dots: css`
    height: 36px;
    border-radius: var(--cds-border-radius-max);
    background: var(--cds-color-neutral-background-primary-weak);
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    padding: var(--cds-spacing-100);
  `,
  arrows: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
  arrowButton: css`
    border-radius: 100%;
  `,
  dotsContainerWidth: (numDots: number) => css`
    width: ${numDots * 24}px;
  `,
  dot: css`
    width: 12px;
    height: 12px;
    border-radius: 50%;
  `,
  activeDot: css`
    background-color: var(--cds-color-neutral-primary-weak);
  `,
  inactiveDot: css`
    background-color: var(--cds-color-neutral-disabled-strong);
  `,
};

const MobileHeroSlider = ({ children }: Props) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const isRTL = isRightToLeft(_t.getLocale());

  const handleNext = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: isRTL ? carouselRef.current.offsetWidth * -1 : carouselRef.current.offsetWidth,
        behavior: 'smooth',
      });
    }
  };

  const handlePrevious = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: isRTL ? carouselRef.current.offsetWidth : carouselRef.current.offsetWidth * -1,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (carouselRef.current) {
        const element = carouselRef.current;
        const leftScroll = isRTL ? element.scrollLeft * -1 : element.scrollLeft;
        const newActiveIndex = Math.round(leftScroll / element.offsetWidth);

        // Update active index state only if it has changed
        if (newActiveIndex !== activeIndex) {
          setActiveIndex(newActiveIndex);
        }
      }
    };

    const currentSlide = carouselRef.current;

    currentSlide?.addEventListener('scroll', handleScroll);
    return () => {
      currentSlide?.removeEventListener('scroll', handleScroll);
    };
  }, [activeIndex, isRTL]);

  return (
    <div css={styles.container}>
      <div css={styles.horizontalScrollContainer} ref={carouselRef}>
        {children.map((child, index) => {
          return (
            <div css={styles.cardContainer} key={`slider-card-${index}`}>
              <div css={styles.card}>{child}</div>
            </div>
          );
        })}
      </div>
      <div css={styles.controls}>
        <div css={[styles.dots, styles.dotsContainerWidth(children.length)]}>
          {children.map((_, index) => (
            <div
              css={[styles.dot, index === activeIndex ? styles.activeDot : styles.inactiveDot]}
              key={`slider-display-dot-${index}`}
            />
          ))}
        </div>
        <div css={styles.arrows}>
          <IconButton
            css={styles.arrowButton}
            onClick={handlePrevious}
            variant="secondary"
            intent="previous"
            size="small"
            disabled={activeIndex === 0}
          />
          <IconButton
            css={styles.arrowButton}
            onClick={handleNext}
            variant="secondary"
            intent="next"
            size="small"
            disabled={activeIndex === children.length - 1}
          />
        </div>
      </div>
    </div>
  );
};

export default MobileHeroSlider;
